import React from 'react';
import { useTranslation } from '@infra/i18n';
import { FullScreenError } from './FullScreenErrors';
import { ErrorState, PageWrapper } from './PageWrapper';

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: (error: Error, errorInfo: React.ErrorInfo, resetError: () => void) => React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showFullScreen?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Global Error Boundary component to catch React errors
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Store error info for display
    this.setState({
      errorInfo,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // In production, you could send error to error tracking service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
    if (import.meta.env.PROD) {
      // TODO: Integrate with error tracking service (Sentry, LogRocket, etc.)
      // logErrorToService(error, errorInfo);
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo!, this.handleReset);
      }

      // Use full screen error for critical errors
      if (this.props.showFullScreen) {
        return <FullScreenError code="404" />;
      }

      // Default fallback UI
      return <ErrorBoundaryFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}

interface ErrorBoundaryFallbackProps {
  error: Error;
  onReset: () => void;
}

/**
 * Default fallback UI for ErrorBoundary
 */
function ErrorBoundaryFallback({ error, onReset }: ErrorBoundaryFallbackProps) {
  const { t } = useTranslation();

  return (
    <PageWrapper maxWidth="lg">
      <ErrorState
        title={t('common:messages.error')}
        message={
          import.meta.env.DEV
            ? error.message || t('common:messages.loadFailed')
            : t('common:messages.loadFailed')
        }
        onRetry={onReset}
        actionLabel={t('common:buttons.retry')}
      />
      {import.meta.env.DEV && error.stack && (
        <details className="mt-4 p-4 bg-muted rounded-lg text-xs overflow-auto max-h-64">
          <summary className="cursor-pointer font-semibold mb-2">Error Details (Dev Only)</summary>
          <pre className="whitespace-pre-wrap">{error.stack}</pre>
        </details>
      )}
    </PageWrapper>
  );
}
